import { TableCell, TableRow, Image } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { ColorText, HoverBorderButton, MuteText } from '@elements/index';
import { carrierImageMap, DateFormat, DateUtility, theme } from '@utility/index';
import { DarkPopup, OrganisationAvatar, TextCopyToClipboard } from '@components/index';
import styled from 'styled-components';
import { Note } from '@phosphor-icons/react';
import { Tag, Tooltip } from 'antd';

interface CarrierBadgeProps {
  carrier: string;
}

interface StatusBadgeProps {
  status?: string;
  category: string;
  message?: string;
}

interface NotePreviewProps {
  note?: string;
  maxLength?: number;
}

interface TrackingRowProps {
  item?: ITrackingItem;
  showAcknowledgeButton?: boolean;
  onAcknowledgeClick?: (item: ITrackingItem) => void;
}

export interface ITrackingItem {
  trackingNumber: string;
  carrier: string;
  status: string;
  message: string;
  category: string;
  canRefund: boolean;
  link: string;
  eta: string;
  acknowledged: boolean;
  acknowledgedAt: string; // ISO date string
  acknowledgedNote: string;
  acknowledgedBy?: {
    first_name?: string;
    last_name?: string;
    email?: string;
  };
  statusHistory: IStatusHistory[];
  order: IOrder;
  label: ILabel;
}

interface IStatusHistory {
  _id: string;
  status: string;
  message: string;
  category: string;
  canRefund: boolean;
  link: string;
  eta: string;
  timestamp: string;
  fetchedAt: string;
}

interface IOrder {
  _id: string;
  marketplaceOrderDisplayId: string;
  fulfillmentProviderId: string;
  timeAcknowledged: string;
  organizationId: string;
  organizationName: string;
  organizationLogo?: IOrganizationLogo;
  shippingAddress: IShippingAddress;
  fulfillmentDetails: IFulfillmentDetails;
}

interface IOrganizationLogo {
  key: string;
  bucket_name: string;
  url: string;
}

interface IShippingAddress {
  name: string;
  stateOrRegion: string;
  countryCode: string;
  postalCode: string;
}

interface IFulfillmentDetails {
  fulfillment_provider_id: string;
  fulfilment_provider_order_id: string;
}

interface ILabel {
  _id: string;
  createdAt: string;
  fulfillmentProviderId: string;
}

const CarrierImage = styled(Image)`
  width: 30px;
  height: 30px;
  object-fit: contain;
`;

export const TrackingNumberContainer = styled.div`
  &:hover {
    .copy-icons {
      opacity: 1;
      margin-top: -2px;
    }
  }
  display: flex;
`;

// Carrier Badge Component
export function CarrierBadge({ carrier }: CarrierBadgeProps): JSX.Element {
  return (
    <div className="d-flex flex-column align-items-center">
      {carrierImageMap[carrier?.toLowerCase()] && (
        <CarrierImage src={carrierImageMap[carrier?.toLowerCase()]} />
      )}
      {carrier}
    </div>
  );
}

export function OrderTime({ time, isEnabled }) {
  let timeColor = '';
  const noOfDays = DateUtility.noOfDays(time);
  if (isEnabled) {
    if (noOfDays < 1) {
      timeColor = theme.colors.green;
    } else if (noOfDays <= 2) {
      timeColor = theme.colors.yellow;
    } else if (noOfDays <= 5) {
      timeColor = theme.colors.orange;
    } else if (noOfDays > 5) {
      timeColor = theme.colors.danger;
    }
  }
  return (
    <ColorText color={timeColor}>
      <b>{DateUtility.hourMinuteTime(time)}</b>
    </ColorText>
  );
}

const TruncatedText = styled.span`
  max-width: 150px;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Status Badge Component
export function StatusBadge({
  status,
  category,
  message,
}: StatusBadgeProps): JSX.Element {
  const colorMap = {
    alerts: theme.colors.red,
    in_process_of_delivery: theme.colors.darkBlue,
  };

  const isDelivered = status?.toLowerCase?.() === 'delivered';

  if (isDelivered) {
    return (
      <Tag color={theme.colors.green} className="cursor-pointer">
        <span>Delivered</span>
      </Tag>
    );
  }

  return (
    <Tooltip title={message || status}>
      <Tag color={colorMap[category] || theme.colors.primary}>
        {category === 'in_process_of_delivery' ? 'In Progress' : status}
      </Tag>
    </Tooltip>
  );
}

const NoteIcon = styled(Note)`
  min-width: 16px;
  margin-top: 2px;
  margin-right: 4px;
`;

// Note Preview Component
export function NotePreview({ note }: NotePreviewProps): JSX.Element {
  if (!note) {
    return <MuteText>No note</MuteText>;
  }

  return (
    <DarkPopup
      trigger={
        <div className="d-flex">
          <NoteIcon />
          <TruncatedText>{note}</TruncatedText>
        </div>
      }
    >
      {note}
    </DarkPopup>
  );
}

export function TrackingRow({
  item,
  showAcknowledgeButton,
  onAcknowledgeClick,
}: TrackingRowProps): JSX.Element {
  const handleAcknowledgeClick = () => {
    if (onAcknowledgeClick) {
      onAcknowledgeClick(item);
    }
  };

  const providerId = item.order?.fulfillmentProviderId || '';
  const orderDisplayId = item.order?.marketplaceOrderDisplayId || 'N/A';
  const orderId = item.order?._id || '';

  return (
    <TableRow>
      <TableCell>
        {item.label?.createdAt && (
          <>
            <OrderTime time={item.label?.createdAt} isEnabled />
            <div>
              <MuteText>
                {DateUtility.formatDate(item.label?.createdAt, DateFormat.barChartMonth)}
              </MuteText>
            </div>
          </>
        )}
      </TableCell>
      <TableCell>
        <TrackingNumberContainer>
          <Link to={item.link} target="_blank">
            {item.trackingNumber}
          </Link>
          <TextCopyToClipboard text={item.trackingNumber} showText={false} showIcon />
        </TrackingNumberContainer>
      </TableCell>
      <TableCell>
        {orderId ? (
          <Link to={`/app/fulfill/orders/details/${orderId}/?providerId=${providerId}`}>
            {orderDisplayId}
          </Link>
        ) : (
          <span>{orderDisplayId}</span>
        )}
      </TableCell>
      <TableCell>
        <div className="d-flex justify-content-start align-items-center">
          <OrganisationAvatar
            image={item.order?.organizationLogo?.url}
            name={item.order?.organizationName}
          />
          <TruncatedText>&nbsp;{item.order?.organizationName}</TruncatedText>
        </div>
      </TableCell>
      <TableCell>
        <CarrierBadge carrier={item.carrier} />
      </TableCell>
      <TableCell>
        <StatusBadge
          message={item.message}
          category={item.category}
          status={item.status}
        />
      </TableCell>
      {showAcknowledgeButton && (
        <>
          <TableCell textAlign="center">
            {showAcknowledgeButton &&
              item.category === 'alerts' &&
              !item.acknowledged && (
                <HoverBorderButton
                  className="text-nowrap"
                  onClick={handleAcknowledgeClick}
                >
                  Acknowledge
                </HoverBorderButton>
              )}
          </TableCell>
        </>
      )}

      {/* For Ignored Tab - Acknowledged By, Acknowledged At, Note */}
      {showAcknowledgeButton === false && (
        <>
          <TableCell>
            {item.acknowledgedBy
              ? `${item.acknowledgedBy.first_name || ''} ${
                  item.acknowledgedBy.last_name || ''
                }`.trim() ||
                item.acknowledgedBy.email ||
                'Unknown'
              : 'N/A'}
          </TableCell>
          <TableCell>
            {item.acknowledgedAt
              ? DateUtility.formatDate(item.acknowledgedAt, 'MMM DD, YYYY HH:mm')
              : 'N/A'}
          </TableCell>
          <TableCell>
            <NotePreview note={item.acknowledgedNote} />
          </TableCell>
        </>
      )}
    </TableRow>
  );
}
